import { useAuth } from '@cannect/hooks/AuthContext'
import { usePendingActions } from '@cannect/hooks/usePendingActions'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { useToggleProductFavorite } from '@cannect/services/resources/products'
import {
  getDisplayableTiers,
  useGetProductTiers
} from '@cannect/services/resources/progressive-discounts'
import { BRAND_REMOVED_FROM_ANVISA_MESSAGE, LIST_BRAND_ATTENDANT } from '@cannect/constants/products'
import { RedirectUrls } from '@cannect/constants/texts'
import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import { getPillPmcPromo } from '@cannect/utils/pillPmcPromo'
import { cannectDefaultProduct, cannectDefaultProductNoTarg } from 'assets/img'
import useSignedUser from 'hooks/useSignedUser'
import { useTokenApi } from 'hooks/useTokenApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from 'services/api'
import { toast } from 'react-toastify'

interface UseProductListItemProps {
  product: any
}

const TOPIC_PRODUCT_WARNING_ID = 15757
const TOPIC_PRODUCT_WARNING_MESSAGE =
  'ATENÇÃO: O produto USA Hemp - Full Spectrum - Tópico Miracle Heal é de uso TÓPICO. Em caso de dúvidas, consulte o profissional de saúde que acompanha seu tratamento.'

const PRODUCTS_WITH_EXPIRING_BATCH = [15908, 15912, 15914, 15909]
const EXPIRING_BATCH_WARNING_MESSAGE =
  'Atenção, cliente! O lote disponível deste produto possui vencimento em setembro. Ao adicionar ao carrinho, você está de acordo em receber esse lote. Agradecemos pela compreensão!'

const requiresLoginAtAdd = (p: any) => p?.is_cannabis === true

export const useProductListItem = ({ product }: UseProductListItemProps) => {
  const history = useHistory()
  const { role } = useAuth()
  const { signedUser } = useSignedUser()
  const { tokenApi } = useTokenApi()
  const toggleFavoriteMutation = useToggleProductFavorite()
  const { addToCart, AddToCartByFlavor } = usePrepareCart()
  const { setPendingAction } = usePendingActions()
  const [topicProductWarningModal, setTopicProductWarningModal] = useState<{
    open: boolean
    product?: { id: number; class_id?: number; brand_id?: number; type?: string }
  }>({ open: false })
  const [expiringBatchWarningModal, setExpiringBatchWarningModal] = useState<{
    open: boolean
    product?: { id: number; class_id?: number; brand_id?: number; type?: string }
  }>({ open: false })

  const [flavorModal, setFlavorModal] = useState(false)
  const [loadingOnSave, setLoadingOnSave] = useState(false)
  const [defaultSelectedFlavor, setDefaultSelectedFlavor] = useState<any>({})
  const [hasMultipleFlavors, setHasMultipleFlavors] = useState(false)
  const [anvisaModal, setAnvisaModal] = useState(false)
  const [anvisaModalMessage, setAnvisaModalMessage] = useState('')
  const [modalUrlClickButton, setModalUrlClickButton] = useState('')
  const [learnMore, setLearnMore] = useState(false)

  const isAuthenticated = !!role && role > 0

  const sellable = product?.sellable !== false
  const isPillProduct = product?.origin === 'pill'
  const isSupplementProduct = product?.class_id === 6

  const class_id = product?.class_id
  const supplier_id = product?.supplier_id

  const { data: tiersData } = useGetProductTiers(product?.id, isAuthenticated || isSupplementProduct)

  const discountBadgeLabel = useMemo(() => {
    const isShippingOnly = tiersData?.rule?.benefit_target === 'shipping'
    const maxProgressivePct = !isShippingOnly
      ? Math.round(
          getDisplayableTiers(tiersData?.tiers).reduce(
            (max, tier) => Math.max(max, Number(tier.discount_percent) || 0),
            0
          )
        )
      : 0

    if (tiersData?.has_progressive_discount && maxProgressivePct > 0) {
      return `Até -${maxProgressivePct}% off`
    }

    const regularPrice = Number(product?.real_price) || 0
    const promoPrice = Number(product?.promotion?.promotional_price) || 0
    if (promoPrice > 0 && regularPrice > 0 && promoPrice < regularPrice) {
      const promoPct = Math.round(((regularPrice - promoPrice) / regularPrice) * 100)
      if (promoPct > 0) return `Até -${promoPct}% off`
    }

    const pillPmcPromo = getPillPmcPromo(product)
    if (pillPmcPromo) {
      return `Até -${pillPmcPromo.discountPct}% off`
    }

    return null
  }, [
    tiersData,
    product?.real_price,
    product?.promotion?.promotional_price,
    product?.origin,
    product?.price,
    product?.shopifyMetafields
  ])

  const infoBySupplier = {
    defaultImage: supplier_id === 18 || class_id === 6 ? cannectDefaultProduct : cannectDefaultProductNoTarg
  }

  useEffect(() => {
    const checkMultipleFlavors = async () => {
      if (product?.class?.id === 6) {
        return
      }

      try {
        const { data } = await api.get(`/get_product_flavors/${product.id}`)
        const hasFlavors = data?.success && Array.isArray(data.productFlavors) && data.productFlavors.length > 0
        setHasMultipleFlavors(!!hasFlavors)
      } catch {
        setHasMultipleFlavors(false)
      }
    }

    checkMultipleFlavors()
  }, [product?.id, product?.class?.id])

  const handleLogin = useCallback(() => {
    history.push({ pathname: '/login', state: { from: { pathname: window.location.pathname } } })
  }, [history])

  const handleNotifyMeClick = useCallback(() => {
    if (!isAuthenticated) {
      sessionStorage.setItem(
        '@CANNECT:PENDING_NOTIFY_ME',
        JSON.stringify({ productId: product.id, source: 'portal_card' })
      )
      handleLogin()
      return
    }
    api
      .post(`/product/${product.id}/notify-me`, { source: 'portal_card' })
      .then((res: any) => {
        const alreadyExisted = res?.data?.created === false
        toast.success(
          alreadyExisted
            ? 'Você já estava na lista de aviso para este produto.'
            : 'Você será avisado quando este produto estiver disponível.'
        )
      })
      .catch(() => {
        toast.error('Não foi possível registrar seu interesse. Tente novamente.')
      })
  }, [product.id, isAuthenticated, handleLogin])

  const handleFavoriteClick = useCallback(() => {
    if (!isAuthenticated) {
      setPendingAction({
        type: 'TOGGLE_FAVORITE',
        payload: { productId: product.id }
      })
      handleLogin()
      return
    }
    toggleFavoriteMutation.mutate(product.id)
  }, [isAuthenticated, handleLogin, toggleFavoriteMutation, product.id, setPendingAction])

  const handleShowModal = useCallback((message: string, url: string, learnMoreFlag = false, showFlavorModal = true) => {
    setFlavorModal(showFlavorModal)
    setAnvisaModal(true)
    setModalUrlClickButton(url)
    setLearnMore(learnMoreFlag)
    setAnvisaModalMessage(message)
  }, [])

  const handleSaveFlavor = async (items: any[]) => {
    setLoadingOnSave(true)
    const itemArray = Array.isArray(items) ? items : [items]
    try {
      await AddToCartByFlavor(itemArray)
    } finally {
      setLoadingOnSave(false)
    }
  }

  const handleOkFlavor = () => {
    setLoadingOnSave(false)
    setFlavorModal(false)
  }

  const handleCloseFlavorModal = () => {
    setFlavorModal(false)
  }

  const handleCloseAnvisa = () => {
    setAnvisaModal(false)
  }

  const handleCartClick = useCallback(() => {
    if (!isAuthenticated && requiresLoginAtAdd(product)) {
      setPendingAction({
        type: 'ADD_TO_CART',
        payload: {
          productId: product.id,
          quantity: 1,
          classId: product.class_id,
          brandId: product.brand_id,
          type: product.type
        }
      })
      handleLogin()
      return
    }

    if (product.id === TOPIC_PRODUCT_WARNING_ID) {
      setTopicProductWarningModal({
        open: true,
        product: { id: product.id, class_id: product.class_id, brand_id: product.brand_id, type: product?.type }
      })
      return
    }

    if (PRODUCTS_WITH_EXPIRING_BATCH.includes(product.id)) {
      setExpiringBatchWarningModal({
        open: true,
        product: { id: product.id, class_id: product.class_id, brand_id: product.brand_id, type: product?.type }
      })
      return
    }

    const brandIdForRules = product?.brand?.id ?? product?.brand_id

    if (LIST_BRAND_ATTENDANT.includes(brandIdForRules)) {
      handleShowModal(BRAND_REMOVED_FROM_ANVISA_MESSAGE, RedirectUrls.WHATSAPP_SALES, true, false)
      return
    }

    if (hasMultipleFlavors) {
      setAnvisaModal(false)
      setFlavorModal(true)
      setDefaultSelectedFlavor(product)
      return
    }

    addToCart(product.id, 1, product.class_id, brandIdForRules, product?.type, product?.is_cannabis)
  }, [product, isAuthenticated, handleLogin, addToCart, setPendingAction, handleShowModal, hasMultipleFlavors])

  const handleTopicProductContinue = useCallback(() => {
    const p = topicProductWarningModal.product
    if (p) {
      addToCart(p.id, 1, p.class_id, p.brand_id, p?.type)
    }
    setTopicProductWarningModal({ open: false })
  }, [topicProductWarningModal, addToCart])

  const handleExpiringBatchContinue = useCallback(() => {
    const p = expiringBatchWarningModal.product
    if (p) {
      addToCart(p.id, 1, p.class_id, p.brand_id, p?.type)
    }
    setExpiringBatchWarningModal({ open: false })
  }, [expiringBatchWarningModal, addToCart])

  const handleProductDetails = useCallback(() => {
    const isSupplements = product?.class_id === 6
    if (isSupplements) {
      window.open(`/detalhes-produtos_suplementos/${product.id}`, '_blank')
    } else {
      window.open(`/detalhes-produtos/${product.id}`, '_blank')
    }
  }, [product])

  const handleMarketplaceModalContinue = useCallback(async () => {
    if (hasMultipleFlavors) {
      setAnvisaModal(false)
      setFlavorModal(true)
      setDefaultSelectedFlavor(product)
      return
    }
    try {
      await addToCart(
        product.id,
        1,
        product.class_id,
        product?.brand?.id ?? product?.brand_id,
        product?.type,
        product?.is_cannabis
      )
    } catch (e) {
      console.error(e)
    }
  }, [hasMultipleFlavors, product, addToCart])

  const prescriptionLabel = useMemo(() => {
    if (product.origin === 'pill') return null
    if (product.supplier_id === 18) return 'Entrega imediata!'
    if (product.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(product.brand_id)) return 'Frete grátis'
    if (product.type === 'TESTE_GENETICO') return 'Prescrição não necessária'
    return 'Prescrição necessária'
  }, [product.origin, product.supplier_id, product.class_id, product.brand_id, product.type])

  return {
    handleFavoriteClick,
    handleCartClick,
    handleProductDetails,
    handleNotifyMeClick,
    isAuthenticated,
    sellable,
    isPillProduct,
    topicProductWarningModal,
    setTopicProductWarningModal,
    topicProductWarningMessage: TOPIC_PRODUCT_WARNING_MESSAGE,
    handleTopicProductContinue,
    expiringBatchWarningModal,
    setExpiringBatchWarningModal,
    expiringBatchWarningMessage: EXPIRING_BATCH_WARNING_MESSAGE,
    handleExpiringBatchContinue,
    anvisaModal,
    anvisaModalMessage,
    modalUrlClickButton,
    learnMore,
    handleCloseAnvisa,
    handleMarketplaceModalContinue,
    flavorModal,
    hasMultipleFlavors,
    handleCloseFlavorModal,
    handleOkFlavor,
    handleSaveFlavor,
    loadingOnSave,
    defaultSelectedFlavor,
    tokenApi,
    signedUser,
    infoBySupplier,
    isFavorite: product?.isFavorite || false,
    hasPromotion: !!product.promotion?.promotional_price,
    discountBadgeLabel,
    isSupplementProduct,
    prescriptionLabel,
    buttonText: product.supplier_id === 18 || product.class_id === 6 ? 'Comprar' : 'Adicionar ao carrinho',
    cartQuantity: 0
  }
}
